import React, { Dispatch, SetStateAction } from 'react'
import styled from '@emotion/styled'
import {
  PlusOutlined,
  PaperClipOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import { Tooltip, message, Form, Row, Col, Typography } from 'antd'
import { BulkRewardImportFile } from '../utils'
import { flatMap, uniq } from 'lodash'
import { CSVLink } from 'react-csv'
import { useLazyQuery } from '@apollo/client'
import { CHECK_EMAILS } from '../LoyaltyQueries'
import { useSlerp } from '@slerp/client'
import { DrawerLabel } from './styles'

const { Text } = Typography

interface User {
  id: string
  first_name: string
  last_name: string
  email: string
}

interface InvalidEmail {
  message: string
  list: string[]
}

interface UploadCSVProps {
  invalidEmails: InvalidEmail
  setInvalidEmails: Dispatch<SetStateAction<InvalidEmail>>
  validEmails: User[]
  setValidEmails: Dispatch<SetStateAction<User[]>>
  hasFile: boolean
  setHasFile: Dispatch<SetStateAction<boolean>>
  fileName: string
  setFileName: Dispatch<SetStateAction<string>>
  setShowEmails: Dispatch<SetStateAction<boolean>>
  uploadRef: any
  exclusive?: boolean
}

const UploadCSV = (props: UploadCSVProps) => {
  const {
    validEmails,
    setValidEmails,
    invalidEmails,
    setInvalidEmails,
    hasFile,
    setHasFile,
    fileName,
    setFileName,
    setShowEmails,
    uploadRef,
    exclusive
  } = props
  const { user } = useSlerp()

  const CSVTemplate = [
    [`test1@${user?.merchant?.slug}.com`],
    [`test2@${user?.merchant?.slug}.com`],
    [`test3@${user?.merchant?.slug}.com`],
    [`test4@${user?.merchant?.slug}.com`]
  ]

  const [checkEmails] = useLazyQuery(CHECK_EMAILS, {
    onCompleted: (data) => {
      const { checkEmails } = data
      const { valid: validEmails = [], invalid: invalidEmails = [] } =
        checkEmails
      const invalid = invalidEmails.filter((str: string) => str !== '')
      const hasValid = Boolean(validEmails.length)
      const hasInvalid = Boolean(invalid.length)

      if (hasValid && !hasInvalid) {
        setValidEmails(validEmails)
        setInvalidEmails({
          message: '',
          list: []
        })
        // set invalid modal
      } else if (hasValid && hasInvalid) {
        setValidEmails(validEmails)
        setInvalidEmails({
          message: `Your upload contains ${invalid.length} email addresses that do not match your customer database`,
          list: invalid
        })
        message.warning(
          `Your upload contains ${invalid.length} email addresses that do not match your customer database`,
          5
        )
      } else if (!hasValid && hasInvalid) {
        setValidEmails([])
        setInvalidEmails({
          message: `Your upload contains ${invalid.length} email addresses that do not match your customer database`,
          list: invalid
        })
      } else {
        setValidEmails([])
        setInvalidEmails({
          message: '',
          list: []
        })
      }
    },
    onError: (error) =>
      message.error(`Unable to verify customer emails due to: ${error}`),
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  })

  const showEmailsMessage = () => {
    const hasInvalid = Boolean(invalidEmails.list.length)
    const hasValid = Boolean(validEmails.length)
    if (hasInvalid && hasValid) {
      return `Your upload contains ${invalidEmails.list.length} email addresses that do not match your customer database`
    } else if (!hasInvalid && hasValid) {
      return `You have a total of ${validEmails.length} eligible customers who are about to be updated`
    } else {
      return `Your upload contains ${invalidEmails.list.length} email addresses that do not match your customer database`
    }
  }

  return (
    <>
      <input
        ref={uploadRef}
        data-testid='bulk-rewards-csv-upload'
        hidden
        type='file'
        placeholder='Bulk Rewards'
        onChange={async (e) => {
          if (!e.target.files || e.target.files.length === 0) return
          const file = e.target.files[0]
          const type = file.type

          if (type !== 'text/csv')
            return message.error('Please upload a .csv file')

          const emailsCSV = (await BulkRewardImportFile(file)) as string
          const customersList = emailsCSV
            .split('\r\n')
            .map((str) => str.replace(/,\s*$/, '').replace(/"|'/g, '').trim())
            .filter(Boolean) // remove empty values
          const customers = uniq(
            flatMap(customersList.map((email) => email.split(',')))
          ) // used lodash flatMap here instead of what's available for es-2019(discuss upgrade to es-2019 first)

          if (customers.length > 6000 && !!exclusive) {
            return message.error(
              'Please upload a .csv file. A maximum of 6000 customers can be uploaded at once.'
            )
          }

          setFileName(file.name)
          setHasFile(true)

          checkEmails({
            variables: {
              merchantId: user?.merchant?.id ?? '',
              emails: customers
            }
          })
        }}
        accept='.csv'
      />
      <CsvErrorFormItem
        validateStatus={!hasFile ? 'error' : undefined}
        help={
          !hasFile
            ? exclusive
              ? 'Please upload a .csv file. A maximum of 6000 customers can be uploaded at once.'
              : 'Please upload a .csv file'
            : undefined
        }
        name='csv_file'
        className='_mb-0'
        label={
          <>
            <DrawerLabel className='_mr-8'>Upload</DrawerLabel>
            <Tooltip
              title={
                <>
                  Upload a .csv file containing customer emails.
                  <br />
                  <br />
                  Tip:{' '}
                  <i>
                    The amount of rows/columns does not matter, just fill each
                    cell with a valid customer email.
                  </i>
                </>
              }
              placement='right'
              className='_mt-2'
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </>
        }
      >
        <Col>
          {fileName ? (
            <>
              <CsvContainer>
                <div className='csv-title'>
                  <div className='paper-clip'>
                    <PaperClipOutlined />
                  </div>
                  <span>{fileName}</span>
                  <div className='csv-approved'>Approved</div>
                </div>
                <div
                  className='csv-delete'
                  onClick={() => {
                    setValidEmails([])
                    setFileName('')
                    setHasFile(false)
                  }}
                >
                  delete
                </div>
              </CsvContainer>
              {
                <CsvMessage>
                  <div>{showEmailsMessage()}</div>
                  <div onClick={() => setShowEmails(true)}>See details</div>
                </CsvMessage>
              }
            </>
          ) : (
            <BulkUpload onClick={() => uploadRef.current?.click()}>
              <PlusOutlined />
              <span>Upload</span>
            </BulkUpload>
          )}
        </Col>
      </CsvErrorFormItem>
      <Row className='_mb-22'>
        <Text>
          Click{' '}
          <CSVLink
            filename='CSV Template'
            data={CSVTemplate}
            style={{ color: '#D092DD', textDecoration: 'underline' }}
          >
            here
          </CSVLink>{' '}
          to download the CSV template
        </Text>
      </Row>
    </>
  )
}

const BulkUpload = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  width: 300px;
  height: 64px;

  background-color: #f3f2f4;

  border: 1px dashed #d2d2d9;
  border-radius: 4px;

  color: #4a4a68;
  font-weight: 400;
  cursor: pointer;

  *:first-of-type {
    font-weight: 500;
    font-size: 16px;
  }

  span:last-child {
    font-size: 12px;
  }
`

const CsvContainer = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: space-between;
  width: fit-content;
  padding: 10px 8px;

  box-shadow: 0px 1px 2px rgba(74, 74, 104, 0.1);
  border-radius: 4px;

  > .csv-title {
    display: flex;
    align-items: center;
    gap: 8px;

    line-height: 1;
  }

  > .csv-title > span {
    color: #4a4a68;
    font-weight: 500;
    font-size: 0.75rem;

    max-width: 40ch;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  .paper-clip {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;

    border-radius: 4px;
    background-color: #d092dd;
  }

  .paper-clip > span[aria-label='paper-clip'] > svg[data-icon='paper-clip'] {
    fill: white;
  }

  .csv-approved {
    color: #007b65;
    border-radius: 4px;
    border: 1px;
    border-style: solid;
    border-color: #dbd9df;
    padding: 5px 16px;
    margin-left: 25px;
  }

  .csv-delete {
    position: absolute;
    right: -56px;

    text-decoration: underline;
    text-transform: capitalize;
    color: #d092dd;
    cursor: pointer;
    user-select: none;

    transition: color 200ms;

    &:hover,
    &:active {
      color: #4a4a68;
    }
  }
`

const CsvMessage = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;

  margin-top: 8px;
  background-color: #f3f2f4;
  border-radius: 4px;

  div:first-of-type {
    max-width: 40ch;

    text-transform: uppercase;
    font-size: 10px;
    font-weight: 600;
  }

  div:last-child {
    color: #d092dd;
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
    user-select: none;

    margin-left: 0.75rem;

    transition: color 200ms;

    &:hover,
    &:active {
      color: #4a4a68;
    }
  }
`

const CsvErrorFormItem = styled(Form.Item)`
  > .ant-col > .ant-form-item-explain.ant-form-item-explain-error {
    margin-bottom: 8px;
    max-height: 1rem;
    min-height: 1rem;
  }

  > .ant-row {
    margin-bottom: 8px;
  }
`

export default UploadCSV
